import { Component, type ErrorInfo, type ReactNode } from 'react';

/**
 * A last line of defence for the shared page.
 *
 * Every other screen in this app is looked at by its owner, on a device they
 * control, with the console a keystroke away. The share page is the exception:
 * it renders on a clinician's machine, in whatever browser their workplace
 * gives them, and an uncaught render error there is a blank white page with no
 * explanation and nobody to report it to. The recipient concludes the link is
 * broken; the owner, who can see their own records perfectly well, has no way
 * to find out it ever happened.
 *
 * So this catches and says something true, rather than letting React unmount
 * the tree to nothing. It is deliberately not wired around the owner's screens
 * — there a thrown error should be loud.
 */
interface Props {
  children: ReactNode;
}

interface State {
  failed: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { failed: false };

  static getDerivedStateFromError(): State {
    return { failed: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Nothing here is sent anywhere — this app has no telemetry and should not
    // grow any. The console is for whoever is standing in front of the screen.
    console.error('[shared] render failed:', error, info.componentStack);
  }

  render(): ReactNode {
    if (!this.state.failed) return this.props.children;

    return (
      <main className="app">
        <div className="page-header">
          <h1>This page didn't load</h1>
          <p>
            Something went wrong displaying these records. The link itself is probably fine — it's
            worth trying again, or opening it in a different browser.
          </p>
        </div>
        <button className="btn btn-primary btn-block" onClick={() => window.location.reload()}>
          Try again
        </button>
      </main>
    );
  }
}
